import React, { Fragment, useState } from 'react';
import { Divider, Flex, Stack, createStyles, useMantineTheme, Box } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';

import dt from '~/domains/contest/domains/manage/domains/common/testing.const';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Text } from '~/domains/design-system/Text';
import { LinkButton } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { FormattedErrors } from '../../../context/utils/formatZodErrors';
import type { StaticField } from '../types';

export const useStyles = createStyles((theme) => ({
  description: {
    color: theme.colors.gray2[7],
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  additionalInfo: {
    color: theme.colors.gray2[3],
  },
  button: {
    width: 'fit-content',
    color: theme.colors.gray2[7],
  },
  indicator: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.colors.blue[4],
  },
}));

export function FieldMeta({
  field,
  errors,
  isTouched,
  noPadding,
  info,
  hideLabel = false,
  indicator = false,
}: {
  field: StaticField;
  errors?: string[] | FormattedErrors;
  isTouched?: boolean;
  noPadding?: boolean;
  hideLabel?: boolean;
  indicator?: boolean;
  info?: {
    title: string;
    data: {
      setting: string;
      template?: string;
      description: string;
    }[];
  };
}) {
  const theme = useMantineTheme();
  const [isOpen, setIsOpen] = useState(false);
  const { classes } = useStyles();

  const hasInfo = info?.data.length > 0;
  const isNoPadding = noPadding ?? !(field.label || field.description || (errors && isTouched));

  return (
    <div style={!isNoPadding ? { paddingBottom: theme.other.spacing._8 } : {}}>
      {!hideLabel ? (
        <Stack spacing={theme.other.spacing._2}>
          {field.label && !hasInfo && (
            <Flex align="center" gap={theme.other.spacing._4}>
              {indicator && <Box className={classes.indicator} />}
              <Text
                data-test-id={dt.fieldMeta.title}
                c="gray2.5"
                variant="subhead-small"
                transform="uppercase"
              >
                {field.label} {field?.isRequired && '*'}
              </Text>
            </Flex>
          )}

          {field.label && hasInfo && (
            <LinkButton className={classes.button} onClick={() => setIsOpen(true)}>
              <Flex align="center" gap={theme.other.spacing._4}>
                {indicator && <Box className={classes.indicator} />}
                <Text
                  data-test-id={dt.fieldMeta.title}
                  variant="subhead-small"
                  transform="uppercase"
                >
                  {field.label}
                </Text>
                <IconInfoCircle color="#909296" size={16} />
              </Flex>
            </LinkButton>
          )}

          {field.description && (
            <Text
              data-test-id={dt.fieldMeta.description}
              className={classes.description}
              dangerouslySetInnerHTML={{ __html: toSafeMarkup(field.description) }}
              variant="body-large"
              c="gray2.3"
            />
          )}
          {field?.additionalInfo && (
            <Text
              data-test-id={dt.fieldMeta.additionalInfo}
              className={classes.additionalInfo}
              variant="subhead-small"
            >
              {field.additionalInfo}
            </Text>
          )}
        </Stack>
      ) : null}
      {isTouched && errors && Array.isArray(errors) && (
        <Text data-test-id={dt.fieldMeta.error} variant="label-small" color="orange.3">
          {errors.join(', ')}
        </Text>
      )}
      <Drawer
        title={
          <Title ta="center" variant="headline-large">
            {info?.title}
          </Title>
        }
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        open={isOpen}
      >
        <Stack spacing={theme.other.spacing._12}>
          {info?.data?.map(({ setting, template, description }, i) => (
            <Fragment key={setting}>
              {i > 0 ? (
                <Divider
                  sx={{ borderTopColor: theme.colors.gray2[0] }}
                  /* eslint-disable-line local-rules/use-theme-tokens */ mr={{ base: -theme.other.spacing._16, sm: -20 }}
                  /* eslint-disable-line local-rules/use-theme-tokens */ ml={{ base: -theme.other.spacing._16, sm: -20 }}
                />
              ) : null}
              <Stack spacing={theme.other.spacing._4}>
                <Text variant="subhead-medium">{setting}</Text>
                <Text variant="subhead-x-small" c="#909296">
                  {template}
                </Text>

                <Text
                  className={classes.description}
                  variant="body-medium"
                  dangerouslySetInnerHTML={{ __html: toSafeMarkup(description) }}
                />
              </Stack>
            </Fragment>
          ))}
        </Stack>
      </Drawer>
    </div>
  );
}
